// 推荐功能页面组件
// 用户可以查看推荐链接、推荐统计和奖励历史

import React, { useState, useEffect } from 'react'
import { Copy } from 'lucide-react'
import { api } from '../lib/api'
import Navbar from '../components/Navbar'
import Pagination from '../components/Pagination'
import LoadingSpinner from '../components/LoadingSpinner'

interface ReferredUser {
    id: string
    email: string
    registered_at: string
    total_spent: number
    payment_count: number
}

// 定义类型接口
interface ReferralConfig {
    registration_reward: number
    recharge_percentage: number
    max_reward_per_recharge: number
    max_referrals: number
    first_charge_bonus: number
}

interface ReferralStats {
    this_month_count: number
    last_30_days_count: number
    latest_referral_time: string | null
    conversion_rate: number
}

interface ReferralInfo {
    referral_code: string
    referral_link: string
    referral_count: number
    total_earnings: number
    registration_rewards: number
    recharge_rewards: number
    is_referred_user: boolean
    referral_stats: ReferralStats
    config: ReferralConfig
}

interface RewardHistory {
    id: string
    reward_type: 'registration' | 'recharge'
    reward_amount: number
    recharge_amount: number
    created_at: string
    referred_email: string
}


const Referrals: React.FC = () => {
    const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null)
    const [rewardHistory, setRewardHistory] = useState<RewardHistory[]>([])

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [copied, setCopied] = useState(false)
    const [showUsersModal, setShowUsersModal] = useState(false)
    const [modalUsers, setModalUsers] = useState<ReferredUser[]>([])
    const [modalLoading, setModalLoading] = useState(false)
    const [modalPage, setModalPage] = useState(1)
    const [modalTotalPages, setModalTotalPages] = useState(0)
    const [modalTotalRecords, setModalTotalRecords] = useState(0)
    const [modalPageSize, setModalPageSize] = useState(10)
    // 新增：奖励弹窗相关状态
    const [showRewardsModal, setShowRewardsModal] = useState(false)
    const [modalRewards, setModalRewards] = useState<RewardHistory[]>([])
    const [rewardsModalLoading, setRewardsModalLoading] = useState(false)
    const [rewardsModalPage, setRewardsModalPage] = useState(1)
    const [rewardsModalTotalPages, setRewardsModalTotalPages] = useState(0)
    const [rewardsModalTotalRecords, setRewardsModalTotalRecords] = useState(0)
    const [rewardsModalPageSize, setRewardsModalPageSize] = useState(10)

    // 获取推荐信息
    const fetchReferralInfo = async () => {
        try {
            const response = await api.get('/api/referral/info')
            setReferralInfo(response.data.data)
        } catch (err) {
      
            setError('Failed to load referral information')
        }
    }

    // 获取奖励历史
    const fetchRewardHistory = async () => {
        try {
            const response = await api.get('/api/referral/rewards?limit=10')
            setRewardHistory(response.data.data || [])
        } catch (err) {
      
        }
    }

    // 获取推荐用户列表（用于弹窗）
    const fetchReferredUsersForModal = async (page = 1, limit = 10) => {
        try {
            setModalLoading(true)
            const response = await api.get(`/api/referral/users?limit=${limit}&offset=${(page - 1) * limit}`)
            const data = response.data
            
            setModalUsers(data.data || [])
            setModalTotalRecords(data.total || 0)
            setModalTotalPages(Math.ceil((data.total || 0) / limit))
            setModalPage(page)
        } catch (err) {
      
        } finally {
            setModalLoading(false)
        }
    }

    // 获取奖励历史数据（用于弹窗）
    const fetchRewardsForModal = async (page = 1, limit = 10) => {
        try {
            setRewardsModalLoading(true)
            const response = await api.get(`/api/referral/rewards?limit=${limit}&offset=${(page - 1) * limit}`)
            const data = response.data
            
            setModalRewards(data.data || [])
            setRewardsModalTotalRecords(data.total || 0)
            setRewardsModalTotalPages(Math.ceil((data.total || 0) / limit))
            setRewardsModalPage(page)
        } catch (err) {
      
        } finally {
            setRewardsModalLoading(false)
        }
    }

    // 计算奖励统计数据
    const calculateRewardStats = () => {
        if (!rewardHistory || rewardHistory.length === 0) {
            return {
                thisMonthRewards: 0,
                last30DaysEarned: 0,
                latestRewardTime: null,
                totalEarned: referralInfo?.total_earnings || 0
            }
        }

        const now = new Date()
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        const last30Days = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000))

        const thisMonthRewards = rewardHistory.filter(reward => 
            new Date(reward.created_at) >= thisMonthStart
        ).length

        const last30DaysEarned = rewardHistory
            .filter(reward => new Date(reward.created_at) >= last30Days)
            .reduce((sum, reward) => sum + parseFloat(reward.reward_amount.toString()), 0)

        const latestRewardTime = rewardHistory.length > 0 ? rewardHistory[0].created_at : null

        return {
            thisMonthRewards,
            last30DaysEarned: last30DaysEarned.toFixed(2),
            latestRewardTime,
            totalEarned: referralInfo?.total_earnings || 0
        }
    }

    const rewardStats = calculateRewardStats()


    useEffect(() => {
        const loadData = async () => {
            setLoading(true)
            await Promise.all([
                fetchReferralInfo(),
                fetchRewardHistory(),
            ])
            setLoading(false)
        }
        loadData()
    }, [])

    // 复制推荐链接
    const copyReferralLink = async () => {
        if (!referralInfo?.referral_link) return

        try {
            await navigator.clipboard.writeText(referralInfo.referral_link)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
      
        }
    }

    if (loading) {
        return (
            <>
                <Navbar />
                <LoadingSpinner size="medium" text="Loading referral data..." fullPage={true} />
            </>
        )
    }

    if (error) {
        return (
            <>
                <Navbar />
                <div className="bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="text-center py-12">
                            <p className="text-red-600">{error}</p>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <Navbar />
            <div className="bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-8">

                    {/* 上半部分：左右布局 */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
                        {/* 左侧：四宫格统计卡片 */}
                        <div className="xl:col-span-1">
                            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg h-full flex flex-col">
                                <h2 className="text-lg font-bold text-gray-900 mb-6 sm:mb-8">Statistics Overview</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 flex-1">
                                    {/* Total Referrals */}
                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-2">Total Referrals</p>
                                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{referralInfo?.referral_count || 0}</p>
                                    </div>
                                    
                                    {/* Total Earnings */}
                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-2">Total Earnings</p>
                                        <p className="text-xl sm:text-2xl font-bold text-gray-900">${referralInfo?.total_earnings || 0}</p>
                                        <p className="text-xs text-gray-500 mt-2">Added to wallet</p>
                                    </div>
                                    
                                    {/* Registration Rewards */}
                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-2">Registration Rewards</p>
                                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{referralInfo?.registration_rewards || 0}</p>
                                    </div>
                                    
                                    {/* Recharge Rewards */}
                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
                                        <p className="text-xs sm:text-sm font-medium text-gray-600 mb-2">Recharge Rewards</p>
                                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{referralInfo?.recharge_rewards || 0}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 右侧：推荐链接卡片 */}
                        <div className="xl:col-span-2">
                            <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg h-full flex flex-col">
                                <h2 className="text-lg font-bold text-gray-900 mb-3">Your Referral Link</h2>

                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                     <div className="mb-4">
                                         <p className="text-xs text-gray-600 mb-1">Referral Code:</p>
                                         <p className="text-sm sm:text-lg font-mono font-bold text-indigo-600 break-all">
                                             {referralInfo?.referral_code || 'Loading...'}
                                         </p>
                                     </div>
                                     <div>
                                         <p className="text-sm text-gray-600 mb-2">Referral Link:</p>
                                         <div className="flex flex-col sm:flex-row gap-2">
                                             <input
                                                 type="text"
                                                 value={referralInfo?.referral_link || ''}
                                                 readOnly
                                                 className="flex-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-lg px-3 py-2"
                                             />
                                             <button
                                                 onClick={copyReferralLink}
                                                 className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${copied
                                                     ? 'bg-green-100 text-green-700 border border-green-300'
                                                     : 'bg-gray-900 text-white hover:bg-gray-800'
                                                     }`}
                                             >
                                                 <Copy className="h-4 w-4 inline mr-1 sm:mr-2" />
                                                 <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
                                             </button>
                                         </div>
                                     </div>
                                 </div>

                                 {/* How It Works 紧凑版 */}
                                 <div className="bg-gray-50 rounded-lg p-3 flex-1 flex flex-col justify-center">
                                     <h4 className="text-sm font-semibold text-gray-900 mb-2">How It Works</h4>
                                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                         <div className="text-center">
                                             <div className="bg-indigo-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2">
                                                 <span className="text-indigo-600 font-bold text-xs">1</span>
                                             </div>
                                             <p className="text-xs font-medium text-gray-900 mb-1">Share Your Link</p>
                                             <p className="text-xs text-gray-600">Send your referral link to friends</p>
                                         </div>
                                         <div className="text-center">
                                             <div className="bg-indigo-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2">
                                                 <span className="text-indigo-600 font-bold text-xs">2</span>
                                             </div>
                                             <p className="text-xs font-medium text-gray-900 mb-1">They Sign Up & Pay</p>
                                             <p className="text-xs text-gray-600">Friends register and make first payment</p>
                                         </div>
                                         <div className="text-center">
                                             <div className="bg-indigo-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2">
                                                 <span className="text-indigo-600 font-bold text-xs">3</span>
                                             </div>
                                             <p className="text-xs font-medium text-gray-900 mb-1">You Earn Rewards</p>
                                             <p className="text-xs text-gray-600">
                                                 Get ${referralInfo?.config?.registration_reward || 5} + {referralInfo?.config?.recharge_percentage || 10}% commission
                                             </p>
                                         </div>
                                     </div>
                                 </div>
                            </div>
                        </div>
                     </div>



                    {/* Recent Referrals 统计卡片 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-white rounded-2xl p-6 shadow-lg flex flex-col">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Referrals</h3>

                            <div className="space-y-4 flex-1 pb-4">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">This Month:</span>
                                    <span className="text-sm font-medium text-gray-900">{referralInfo?.referral_stats?.this_month_count || 0} new</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Last 30 Days:</span>
                                    <span className="text-sm font-medium text-gray-900">{referralInfo?.referral_stats?.last_30_days_count || 0} total</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Latest:</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {referralInfo?.referral_stats?.latest_referral_time
                                            ? new Date(referralInfo.referral_stats.latest_referral_time).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })
                                            : 'No referrals yet'
                                        }
                                    </span>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-200 mt-auto">
                                <div className="flex justify-center">
                                     <button
                                          onClick={() => {
                                              setShowUsersModal(true)
                                              fetchReferredUsersForModal(1)
                                          }}
                                         className="bg-gray-900 text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors"
                                     >
                                         View All Referrals
                                     </button>
                                 </div>
                            </div>
                        </div>
 {/* Recent Rewards 统计卡片 */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg flex flex-col">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Rewards</h3>

                            <div className="space-y-4 flex-1 pb-4">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">This Month:</span>
                                    <span className="text-sm font-medium text-gray-900">{rewardStats.thisMonthRewards} rewards</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Last 30 Days:</span>
                                    <span className="text-sm font-medium text-gray-900">${rewardStats.last30DaysEarned} earned</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Latest:</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {rewardStats.latestRewardTime
                                            ? new Date(rewardStats.latestRewardTime).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })
                                            : 'No rewards yet'
                                        }
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Total Earned:</span>
                                    <span className="text-sm font-medium text-gray-900">${rewardStats.totalEarned}</span>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-200 mt-auto">
                                <div className="flex justify-center">
                                     <button
                                         onClick={() => {
                                             setShowRewardsModal(true)
                                             fetchRewardsForModal(1)
                                         }}
                                         className="bg-gray-900 text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors"
                                     >
                                         View All Rewards
                                     </button>
                                 </div>
                            </div>
                        </div>
                        </div>

                    {/* 推荐限制提示 */}
                    {referralInfo && referralInfo.referral_count >= (referralInfo.config?.max_referrals || 100) * 0.8 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-700">
                                <strong>Notice:</strong> You have referred {referralInfo.referral_count} out of {referralInfo.config?.max_referrals || 100} maximum allowed referrals.
                                {referralInfo.referral_count >= (referralInfo.config?.max_referrals || 100) &&
                                    ' You have reached the referral limit.'
                                }
                            </p>
                        </div>
                    )}

                    {/* 被推荐用户提示 */}
                    {referralInfo?.is_referred_user && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-700">
                                <strong>Welcome!</strong> You were referred by another user.
                                You'll receive ${referralInfo.config?.first_charge_bonus || 2} bonus on your first payment!
                            </p>
                        </div>
                    )}

                </div>
            </div>
            {/* 推荐用户列表弹窗 */}
            {showUsersModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-base font-semibold text-gray-900">All Referred Users</h3>
                                <button
                                    onClick={() => setShowUsersModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[70vh]">
                            {modalLoading && modalUsers.length === 0 ? (
                            <div className="text-center py-8">
                                <LoadingSpinner size="medium" text="Loading users..." />
                            </div>
                        ) : modalUsers.length > 0 ? (
                                <div className="space-y-2">
                                    {modalUsers.map((user) => (
                                        <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                                <p className="text-xs text-gray-600">
                                                    Joined: {new Date(user.registered_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    user.payment_count > 0 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {user.payment_count > 0 ? 'Paid' : 'Unpaid'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}

                                    {modalTotalPages > 1 && (
                                        <div className="pt-4 border-t border-gray-200">
                                            <Pagination
                                                currentPage={modalPage}
                                                totalPages={modalTotalPages}
                                                totalRecords={modalTotalRecords}
                                                pageSize={modalPageSize}
                                                onPageChange={(page) => fetchReferredUsersForModal(page, modalPageSize)}
                                                onPageSizeChange={(size) => {
                                                    setModalPageSize(size)
                                                    fetchReferredUsersForModal(1, size)
                                                }}
                                                loading={modalLoading}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">No referrals yet</p>
                                    <p className="text-sm text-gray-500">Start sharing your link to see referrals here</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* 奖励历史弹窗 */}
            {showRewardsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-base font-semibold text-gray-900">All Reward History</h3>
                                <button
                                    onClick={() => setShowRewardsModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-y-auto max-h-[70vh]">
                            {rewardsModalLoading && modalRewards.length === 0 ? (
                            <div className="text-center py-8">
                                <LoadingSpinner size="medium" text="Loading rewards..." />
                            </div>
                        ) : modalRewards.length > 0 ? (
                                <div className="space-y-2">
                                    {modalRewards.map((reward) => (
                                        <div key={reward.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                            <div>
                                                <div className="flex items-center">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${reward.reward_type === 'registration'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-green-100 text-green-800'
                                                        }`}>
                                                        {reward.reward_type === 'registration' ? 'Registration' : 'Recharge'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-0.5">
                                                    From: {reward.referred_email}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(reward.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-green-600">+${reward.reward_amount}</p>
                                                {reward.recharge_amount > 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        From ${reward.recharge_amount} payment
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {rewardsModalTotalPages > 1 && (
                                        <div className="pt-4 border-t border-gray-200">
                                            <Pagination
                                                currentPage={rewardsModalPage}
                                                totalPages={rewardsModalTotalPages}
                                                totalRecords={rewardsModalTotalRecords}
                                                pageSize={rewardsModalPageSize}
                                                onPageChange={(page) => fetchRewardsForModal(page, rewardsModalPageSize)}
                                                onPageSizeChange={(size) => {
                                                    setRewardsModalPageSize(size)
                                                    fetchRewardsForModal(1, size)
                                                }}
                                                loading={rewardsModalLoading}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">No rewards yet</p>
                                    <p className="text-sm text-gray-500">Your rewards will appear here when you earn them</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default Referrals